import React, { useMemo, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Tag, Input, Text, Box } from "@chakra-ui/react";
import { pageLabel } from "../../../utils/pageLabels";
import { formatBytes, formatDuration, formatTime } from "./format";

function resourceSummary(res) {
  if (!res) return "";
  const parts = [];
  if (res.file) parts.push(res.file);
  if (res.room) parts.push(res.room);
  if (res.patient) parts.push(`patient ${res.patient}`);
  if (res.pages) parts.push(`${res.pages}${res.total_pages ? `/${res.total_pages}` : ""} pages`);
  const bytes = res.bytes || res.hydrated_bytes || res.snapshot_bytes;
  if (bytes) parts.push(formatBytes(bytes));
  if (res.chars) parts.push(`${res.chars.toLocaleString()} chars`);
  if (res.doc_cache_chars) parts.push(`cache ${res.doc_cache_chars.toLocaleString()} chars`);
  if (res.status) parts.push(res.status);
  return parts.join(" · ");
}

export default function HeavyRequestsTable({ requests }) {
  const [filter, setFilter] = useState("");
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (requests || []).filter((r) => !q || [r.path, r.user_email, r.user_id, r.client_page, resourceSummary(r.resource)]
      .some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [requests, filter]);

  return (
    <Box>
      <Input size="sm" mb={2} maxW="360px" placeholder="Filter by user, action, or file…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {rows.length === 0 && <Text fontSize="sm" color="gray.500">No heavy requests recorded in this window.</Text>}
      <Box overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Time</Th><Th>User</Th><Th>Page</Th><Th>Backend action</Th><Th isNumeric>Took</Th>
              <Th isNumeric>Data</Th><Th isNumeric>Memory added</Th><Th isNumeric>Others running</Th><Th>What it loaded</Th><Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td whiteSpace="nowrap">{formatTime(r.started_at)}</Td>
                <Td><Text fontSize="xs">{r.user_email || r.user_id || "no signed-in user"}</Text></Td>
                <Td><Text fontSize="xs">{r.client_page ? pageLabel(r.client_page) : "—"}</Text></Td>
                <Td>
                  {r.kind === "job" && <Tag size="sm" colorScheme="orange" mr={1}>BACKGROUND JOB</Tag>}
                  <Text as="span" fontSize="xs" fontFamily="mono">{r.path}</Text>
                </Td>
                <Td isNumeric>{formatDuration(r.duration_ms)}</Td>
                <Td isNumeric>{formatBytes((r.request_bytes || 0) + (r.response_bytes || 0))}</Td>
                <Td isNumeric color={(r.rss_delta_mb || 0) >= 20 ? "red.500" : undefined}>
                  {r.rss_delta_mb != null ? `${r.rss_delta_mb > 0 ? "+" : ""}${Math.round(r.rss_delta_mb)} MB` : "—"}
                </Td>
                <Td isNumeric>{r.in_flight ?? "—"}</Td>
                <Td><Text fontSize="xs">{resourceSummary(r.resource)}</Text></Td>
                <Td>{r.status}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
